import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  CpuChipIcon,
  CubeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SignalIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import {
  FaqAccordion,
  PageCta,
  PageHero,
  SectionHeading,
} from '../../components/page-templates/SitePageTemplates';

const heroPanelItems = [
  {
    icon: ShieldCheckIcon,
    title: 'Warehousing for high-value ICT',
    description: 'Secure storage and handling for servers, networking, storage, and telecom equipment.',
  },
  {
    icon: BuildingStorefrontIcon,
    title: 'Singapore flagship at Changi FTZ',
    description: 'Flagship facility at the Changi Airport Free Trade Zone supports global technology logistics.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Global fulfilment network',
    description: 'A worldwide warehouse network managed through a single operational framework and dashboard.',
  },
];

const warehousingRequirements = [
  {
    icon: ShieldCheckIcon,
    title: 'Secure storage for high-value assets',
    description: 'Controlled handling protocols for ICT hardware that cannot be treated as generic pallet storage.',
  },
  {
    icon: PaperAirplaneIcon,
    title: 'Bonded storage and fast release',
    description: 'Duty-suspended storage with quick clearance, so hardware is ready for the next flight out.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Inventory visibility across regions',
    description: 'A unified view of global stock supports faster decisions and shipment readiness.',
  },
  {
    icon: TruckIcon,
    title: 'Coordination with customs and freight',
    description: 'Warehousing aligned with international transport and trade compliance requirements.',
  },
];

const serviceCards = [
  {
    icon: ShieldCheckIcon,
    title: 'Bonded Warehousing',
    description: 'Use our bonded flagship warehouses in Singapore and Jebel Ali.',
    features: [
      'Fast clearance (max 15 minutes)',
      'Loading onto the aircraft within 6 hours',
      'NFO (next flight out)',
      'Staging possibilities within the Free Trade Zone',
    ],
  },
  {
    icon: BuildingStorefrontIcon,
    title: 'Global Warehousing',
    description: 'Secure storage for ICT infrastructure across strategically located facilities.',
    features: [
      'Warehousing for servers, networking, and telecom equipment',
      'Short- and long-term storage options',
      'Secure handling for high-value hardware',
      'Global warehouse partner network managed by TrueLog',
      'Centralised operational control through TrueLog systems',
    ],
  },
  {
    icon: CubeIcon,
    title: 'Global Fulfilment',
    description: 'Efficient inventory management and worldwide distribution.',
    features: [
      'Order fulfilment and shipment coordination',
      'Global inventory visibility through a unified dashboard',
      'Standardised service level agreements (SLAs)',
      'White glove delivery options',
      'Integration with transport and trade compliance services',
    ],
  },
];

const changiBenefits = [
  'Immediate proximity to a major international air cargo hub',
  'Faster international shipment processing',
  'Reduced or deferred customs duties through FTZ benefits',
  'Ideal environment for handling high-value ICT equipment',
];

const PHOTO_DIR = `${process.env.PUBLIC_URL}/assets/images/warehouse`;

// Photos of the Singapore flagship (Changi Airport FTZ), shown under the
// flagship section: one wide shot towards the airfield, then three portrait shots.
const flagshipPhotos = [
  { file: 'changi-ftz-racking.jpg', caption: 'Pallet racking for networking equipment' },
  { file: 'changi-ftz-forklifts.jpg', caption: 'Our own forklift fleet' },
  { file: 'changi-ftz-loading-bay.jpg', caption: 'Trucks load straight from the bay' },
];

const supportedHardware = [
  { icon: BuildingOffice2Icon, label: 'Data centre infrastructure' },
  { icon: ServerStackIcon, label: 'Servers and storage' },
  { icon: SignalIcon, label: 'Networking equipment' },
  { icon: TruckIcon, label: 'Telecom hardware' },
  { icon: CpuChipIcon, label: 'End-point devices and peripherals' },
  { icon: GlobeAltIcon, label: 'Branch office and remote-site equipment' },
];

const faqs = [
  {
    question: 'Where are TrueLog warehouses located?',
    answer:
      'TrueLog operates a flagship warehouse in the Changi Airport Free Trade Zone in Singapore and manages a global network of warehouse partners in strategic logistics locations. Our Singapore address is 7 Airline Road, #02-17 & #02-18, Cargo Agents Building E, Singapore 819834.',
  },
  {
    question: 'Can TrueLog store high-value ICT hardware?',
    answer:
      'Yes. TrueLog warehouses are designed to handle high-value technology equipment such as servers, networking devices, and telecom infrastructure.',
  },
  {
    question: 'Does TrueLog manage global fulfilment?',
    answer:
      'Yes. TrueLog provides fulfilment services, including inventory management, order processing, and global distribution through its warehouse network.',
  },
  {
    question: 'Can TrueLog handle returns and RMA logistics?',
    answer:
      'Yes. TrueLog manages the full Return Merchandise Authorisation (RMA) process, including receiving returned equipment, inspection, repair coordination, and redistribution.',
  },
];

const ctaPills = [
  'Warehousing for high-value ICT hardware',
  'Singapore flagship at Changi FTZ',
  'Global fulfilment through a single framework',
];

const GlobalFulfillment: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Warehousing & Fulfilment"
        title="Where Readiness Meets Scale: Global Warehousing and Fulfilment for ICT Equipment"
        highlight="Global Warehousing and Fulfilment for ICT Equipment"
        description="Store and distribute ICT hardware through secure warehouse facilities designed for technology equipment. TrueLog provides warehousing and fulfilment services for servers, networking equipment, and high-value ICT infrastructure, ensuring safe storage, inventory visibility, and efficient global distribution."
        heroIcon={BuildingStorefrontIcon}
        accentIcon={GlobeAltIcon}
        actions={[{ href: '/contact-us', label: 'Talk to a Specialist' }]}
        badges={[
          'Warehousing for high-value ICT hardware',
          'Singapore flagship warehouse at Changi Airport Free Trade Zone',
          'Global fulfilment network managed through a single operational framework',
        ]}
        metrics={[
          { value: 'Changi FTZ', label: 'Flagship hub' },
          { value: '150+', label: 'Country fulfilment reach' },
          { value: '1', label: 'Unified operating model' },
          { value: '24/7', label: 'Inventory visibility' },
        ]}
        panelEyebrow="Fulfilment architecture"
        panelTitle="The warehouse layer behind controlled deployment"
        panelDescription="Storage, returns, and outbound distribution aligned before assets ever reach the final mile."
        panelItems={heroPanelItems}
      />

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Why It Matters"
            title="Why ICT Hardware Requires Specialist Warehousing"
            description="Technology infrastructure requires more than standard storage. Servers, networking equipment, and telecom hardware are high-value assets that require controlled handling, specialised packaging, and secure inventory management."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            Warehousing for ICT equipment often involves:
          </motion.p>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {warehousingRequirements.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-12 flex max-w-3xl flex-col items-center gap-5 text-center"
          >
            <p className="text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog warehouses are designed to support the <span className="font-semibold text-slate-950">global movement and management of ICT infrastructure</span>, ensuring equipment is handled safely and prepared for onward shipment.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Talk to a Specialist
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Our Services"
            title="Our Warehousing & Fulfilment Services"
            description="Three connected services that combine bonded storage, a global warehouse network, and fulfilment under one operational framework."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {serviceCards.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="flex flex-col rounded-[30px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex w-fit rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                  <service.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-slate-950">{service.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>
                <ul className="mt-5 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                      <CheckCircleIcon className="mt-1 h-4 w-4 flex-shrink-0 text-[#0E9ED5]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact-us"
                  className="mt-auto inline-flex w-fit items-center gap-2 pt-6 text-sm font-bold uppercase tracking-[0.14em] text-[#2447d8] hover:text-[#0E9ED5]"
                >
                  Get a Quote
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
          >
            <span className="eyebrow-pill">Flagship Hub</span>
            <h2 className="mt-5 text-[30px] font-bold tracking-tight text-slate-950 sm:text-[38px]">
              Singapore Flagship Warehouse
            </h2>
            <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog operates a flagship warehouse located within the <span className="font-semibold text-slate-950">Changi Airport Free Trade Zone (FTZ)</span> in Singapore. This strategic location provides major advantages for global technology logistics:
            </p>

            <p className="mt-8 text-base leading-8 text-slate-600 sm:text-lg">
              The Singapore facility also serves as a testing ground for new logistics services before they are introduced across the global warehouse network.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="rounded-[32px] border border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.85)] sm:p-9"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5">
                <MapPinIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Changi Airport FTZ, Singapore</p>
                <p className="mt-1 text-xs leading-5 text-slate-400">7 Airline Road, #02-17 &amp; #02-18, Cargo Agents Building E, Singapore 819834</p>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {changiBenefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="flex items-start gap-3 rounded-[22px] border border-white/10 bg-white/[0.06] px-5 py-4"
                >
                  <PaperAirplaneIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                  <p className="text-sm leading-7 text-slate-200">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="container-custom max-w-7xl mt-12">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="relative overflow-hidden rounded-[32px] shadow-[0_28px_70px_-42px_rgba(15,23,42,0.6)]"
          >
            <img
              src={`${PHOTO_DIR}/changi-ftz-airside.jpg`}
              alt="Inside the TrueLog flagship warehouse at Changi Airport, looking out to the airfield"
              loading="lazy"
              className="h-[260px] w-full object-cover sm:h-[420px] lg:h-[520px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />
            <figcaption className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Changi Airport Free Trade Zone, Singapore</p>
              <p className="mt-2 text-xl font-semibold text-white sm:text-2xl">Bonded, inside the free trade zone, minutes from the aircraft</p>
            </figcaption>
          </motion.figure>

          <div className="mt-3 grid grid-cols-3 gap-3 sm:mt-4 sm:gap-4">
            {flagshipPhotos.map((photo, index) => (
              <motion.figure
                key={photo.file}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                className="relative overflow-hidden rounded-[24px] shadow-[0_20px_50px_-36px_rgba(15,23,42,0.5)]"
              >
                <img
                  src={`${PHOTO_DIR}/${photo.file}`}
                  alt={photo.caption}
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-3 text-xs font-semibold text-white sm:p-4 sm:text-sm">
                  {photo.caption}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Integrated Model"
            title="Built for Global ICT Logistics"
            description="Warehousing is a critical layer of the global ICT supply chain."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-base leading-8 text-slate-600 sm:text-lg"
          >
            TrueLog integrates <span className="font-semibold text-slate-950">storage, transport, and trade compliance</span>, ensuring that ICT hardware moves efficiently from storage to its final destination.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            We support the warehousing and fulfilment of:
          </motion.p>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {supportedHardware.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-center gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="rounded-2xl bg-sky-50 p-3 text-sky-600">
                  <item.icon className="h-6 w-6" />
                </div>
                <p className="text-base font-semibold text-slate-950">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="FAQ"
            title="Common questions about warehousing and fulfilment"
            description="The operational questions that come up most often when planning global storage and distribution of ICT hardware."
          />
          <div className="mx-auto max-w-4xl">
            <FaqAccordion items={faqs} />
          </div>
          <div className="mt-10 flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
              <ArrowPathIcon className="h-4 w-4 text-[#0E9ED5]" />
              RMA and reverse logistics supported end-to-end
            </div>
          </div>
        </div>
      </section>

      <PageCta
        title="Store and Distribute Your ICT Hardware Globally"
        description="Whether you need secure storage, bonded warehousing, or global fulfilment, TrueLog provides the infrastructure required to manage technology equipment across international supply chains."
        pills={ctaPills}
        primaryHref="/contact-us"
        primaryLabel="Contact Us"
        secondaryHref="/services"
        secondaryLabel="Explore services"
      />
    </div>
  );
};

export default GlobalFulfillment;
